'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import type { TooltipProps } from 'recharts';

const ResponsiveContainer = dynamic(() => import('recharts').then((m) => m.ResponsiveContainer), { ssr: false });
const BarChart = dynamic(() => import('recharts').then((m) => m.BarChart), { ssr: false });
const Bar = dynamic(() => import('recharts').then((m) => m.Bar), { ssr: false });
const XAxis = dynamic(() => import('recharts').then((m) => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then((m) => m.YAxis), { ssr: false });
const CartesianGrid = dynamic(() => import('recharts').then((m) => m.CartesianGrid), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then((m) => m.Tooltip), { ssr: false });
const Cell = dynamic(() => import('recharts').then((m) => m.Cell), { ssr: false });

const strategyData = [
  { strategy: 'Breakout', pnl: 5840, trades: 14 },
  { strategy: 'Momentum', pnl: 4120, trades: 11 },
  { strategy: 'VWAP', pnl: 3290, trades: 9 },
  { strategy: 'Gap Fill', pnl: 2180, trades: 7 },
  { strategy: 'Mean Rev', pnl: -1048, trades: 6 },
];

function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value as number;
  const isPositive = val >= 0;
  const entry = strategyData.find((d) => d.strategy === label);
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs font-600 text-foreground mb-1">{label}</p>
      <p className={`text-sm font-600 font-mono-nums ${isPositive ? 'text-positive' : 'text-negative'}`}>
        {isPositive ? '+' : ''}${val.toLocaleString('en-US')}
      </p>
      {entry && <p className="text-xs text-muted-foreground mt-0.5">{entry.trades} trades</p>}
    </div>
  );
}

export default function PnlByStrategyChart() {
  return (
    <div className="bg-card border border-border rounded-xl p-5 card-glow-primary h-full">
      <div className="mb-4">
        <h3 className="text-sm font-600 text-foreground">P&L by Strategy</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Net profit per strategy tag</p>
      </div>

      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={strategyData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            />
            <YAxis
              type="category"
              dataKey="strategy"
              tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
              {strategyData.map((entry, index) => (
                <Cell
                  key={`strategy-bar-${entry.strategy}`}
                  fill={entry.pnl >= 0 ? 'var(--primary)' : 'var(--negative)'}
                  fillOpacity={entry.pnl >= 0 ? 0.85 : 0.75}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}